import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Tree } from 'react-arborist';
import type { VoidFunctionComponent } from 'react';
import type { NodeApi, TreeApi } from 'react-arborist';

import Locator from '../../../../../shared/Locator';
import * as promises from '../../../../../shared/promises';
import { useDresser } from '../../../../contexts/DrawerContext';
import { useParams } from '../../../../contexts/RouterContext';
import { useAddToast } from '../../../../contexts/ToastContext';
import {
  getBinaryComponentPaths,
  getBinaryComponentPathsQueryKey,
} from '../../../../hooks/api/binary/useGetComponentPaths';
import {
  getReleaseGroupComponentPaths,
  getReleaseGroupComponentPathsQueryKey,
} from '../../../../hooks/api/binary/useGetReleaseGroupComponentPaths';
import useDebouncedInputValue from '../../../../hooks/useDebouncedInputValue';
import { useDebouncedResizeObserver } from '../../../../hooks/useDebouncedResizeObserver';
import { useSelectedReleaseId } from '../../../../redux/project_grouping/hooks';
import { toFiniteNumber } from '../../../../utils';
import SearchInput from '../../../generic/Forms/SearchInput';
import ProjectDependencyDrawer from '../../dependenciesV2/DependencyDrawer/ProjectDependencyDrawer';
import ReleaseGroupDependencyDrawer from '../../dependenciesV2/DependencyDrawer/ReleaseGroupDependencyDrawer';
import type { BinaryFileNode, GetBinaryComponentPathsParams } from '../../../../../shared/types/routes/binary';
import type { ReleaseGroupRouteParams } from '../../project_grouping/types';
import type { RevisionRouteParams } from '../../projectDashboardTypes';

import BinaryTreeLoading from './BinaryTreeLoading';
import BinaryTreeNode from './BinaryTreeNode';
import { injectChildren, preloadDirectoryTree, removeNodeFromParent, sortNodes } from './utils';
import type { BinaryTreeNodeData } from './BinaryTreeNode';

import styles from './BinaryAnalysisContainer.module.less';

const DEFAULT_PATHS_TO_OPEN = ['/'];

// We need to display a root node which users can expand.
const ROOT_NODE: BinaryTreeNodeData = { type: 'directory', name: '/', path: '/', children: [] };

type Props = {
  onLoadPath: (path?: string, search?: string) => Promise<BinaryTreeNodeData[]>;
  setSelectedDepRevisionLocator: (locator: string | null) => void;
  setSelectedNode: (node: NodeApi<BinaryTreeNodeData> | null) => void;
  data: BinaryTreeNodeData[];
  setData: (data: BinaryTreeNodeData[]) => void;
  pathsToOpen?: string[];
};

const BinaryTree: VoidFunctionComponent<Props> = ({
  onLoadPath,
  setSelectedDepRevisionLocator,
  setSelectedNode,
  data,
  setData,
  pathsToOpen = DEFAULT_PATHS_TO_OPEN,
}) => {
  const { value: search, onChange: onSearchChange, debouncedValue: debouncedSearch } = useDebouncedInputValue('');
  const addToast = useAddToast();
  const [selectedPath, setSelectedPath] = useState<string>(ROOT_NODE.path);

  const { width, ref } = useDebouncedResizeObserver();

  //  Keep a reference to the tree. This is so that we can properly mark nodes as open/closed in the onToggle.
  const treeRef = useRef<TreeApi<BinaryTreeNodeData>>(null);
  //  This is so that we can do flat lookups when we need to inject nodes.
  //  This doesn't need to be state because it doesn't really power any UI/we don't need the re-render.
  const nodesByPath = useRef<Record<string, BinaryTreeNodeData>>({
    [ROOT_NODE.path]: ROOT_NODE,
  });

  // Whenever the search changes, we reset the tree and close all the nodes.
  useEffect(() => {
    // Reset the tree when the search changes.
    setData([ROOT_NODE]);
    nodesByPath.current = {
      [ROOT_NODE.path]: ROOT_NODE,
    };
    const tree = treeRef.current;
    if (tree) {
      tree.closeAll();
    }
  }, [debouncedSearch, setData]);

  // Register preloaded nodes in nodesByPath ref
  const registerNodeRecursively = useCallback((node: BinaryTreeNodeData) => {
    function register(current: BinaryTreeNodeData) {
      nodesByPath.current[current.path] = current;
      current.children?.forEach(register);
    }
    register(node);
  }, []);

  // Open preloaded paths when they change
  useEffect(() => {
    if (pathsToOpen.length > 0) {
      // First, register all preloaded nodes
      data.forEach(registerNodeRecursively);

      if (treeRef.current) {
        const tree = treeRef.current;
        // Open paths sequentially
        const openPathsSequentially = async () => {
          await promises.each(pathsToOpen, async (path) => {
            if (path) {
              tree.open(path);
            }
          });
        };

        openPathsSequentially();
      }
    }
  }, [pathsToOpen, data, registerNodeRecursively]);

  //  This is a helper function to register the children in the nodesByPath ref.
  //  This is so that we can do flat lookups when we need to inject nodes.
  const registerChildren = (children: BinaryTreeNodeData[]) => {
    children.forEach((child) => {
      nodesByPath.current[child.path] = child;
    });
  };

  //  "inject" the newly loaded children into the tree and update the ref.
  const injectChildrenOnLoad = useCallback((parentPath: string, childNodes: BinaryTreeNodeData[]) => {
    // Sort directories first, then alphabetically by name
    const sortedChildren = [...childNodes].sort(sortNodes);

    //  Put each new child into the map
    registerChildren(sortedChildren);
    //  Now we need to update the parent entry with the updated object (so that it has the children).
    const parentNode = nodesByPath.current[parentPath];
    if (parentNode) {
      nodesByPath.current[parentPath] = {
        ...parentNode,
        children: sortedChildren,
        hasLoaded: true,
      };
    }
    //  Finally, we update the actual state data being passed to the Tree
    setData(injectChildren(data, parentPath, sortedChildren));
  }, [data, setData]);

  //  Method for marking descendants of a node as "closed" within the react-arborist's internal state
  const closeDescendants = useCallback((node: BinaryTreeNodeData) => {
    const tree = treeRef?.current;
    if (!tree) {
      return;
    }
    const close = (current: BinaryTreeNodeData) => {
      current.children?.forEach((child) => {
        tree.close(child.path);
        //  Recurse
        close(child);
      });
    };
    close(node);
  }, [treeRef]);

  //  This is the function which triggers opening/closing a directory.
  //  When a directory is opened, we will manually trigger a fetch with that path.
  const handleToggle = useCallback(async (nodePath: string) => {
    //  Do a flat lookup to find the node.
    const node = nodesByPath.current[nodePath];
    //  Catch undefined, non-directory nodes
    if (!node || node.type !== 'directory') {
      return;
    }

    //  Situation 1: The folder has just been closed.
    //  In this case, we need to manually tell arborist that all of its descendants are also closed.
    const tree = treeRef?.current;
    //  Handle undefined (mostly for TypeScript)
    if (!tree) {
      return;
    }

    //  Check if the node is open AFTER toggling
    const isNowOpen = tree.isOpen(nodePath); // AFTER Arborist toggled
    if (!isNowOpen) {
      //  Tell arborist to close all the descendants
      closeDescendants(node);
      // tree.toggle(nodePath); // Tell arborist to flip it
      return;
    }

    //  Situation 2: The folder was just opened.
    //  In this case, we need to fetch the children of that node (if we haven't already).
    if (node.hasLoaded) {
      return;
    }

    //  This is to fix an issue where arborist needs path to be non-empty but the backend expects empty for root.
    const path = node.name === ROOT_NODE.name ? undefined : node.path;

    const paths = await onLoadPath(path, debouncedSearch);

    //  Handle error case by displaying a toast, then returning early before updating the tree.
    if (!paths) {
      addToast({
        type: 'error',
        content: `Failed to load components for path: ${nodePath}`,
      });
      return;
    }

    if (paths.length === 0) {
      // If no children are returned, remove this node from the tree
      setData(removeNodeFromParent(data, nodePath, 'directory'));
      addToast({
        type: 'info',
        content: `No components found in this directory. ${nodePath !== ROOT_NODE.path ? 'Hiding from tree.' : ''}`,
      });
      return;
    }

    //  "inject" the newly loaded children into the tree at the correct place, and update the ref.
    injectChildrenOnLoad(
      nodePath,
      // oxlint-disable-next-line oxc/no-map-spread -- copy on purpose: `p` is react-query cache data
      paths.map((p) => ({
        ...p,
        //  If `react-arborist` is passed a node with `children` set, it will treat the node as a directory which can be expanded.
        //  We do not want this for cases where it is a file, so we set `children` to `undefined` in that case.
        //  Later when this directory is expanded, we will "inject" the children into the tree.
        children: p.type === 'directory' ? [] : undefined,
        //  For file nodes, append the componentId to make the path unique
        path: p.type === 'file' ? `${p.path}$${p.componentId}` : p.path,
      })),
    );
  }, [injectChildrenOnLoad, closeDescendants, addToast, onLoadPath, data, setData, debouncedSearch]);

  return (
    <>
      <SearchInput value={search} onChange={onSearchChange} size="small" label="binary-tree-search" />
      <div className={styles.treeContainer} ref={ref}>
        <Tree<BinaryTreeNodeData>
          ref={treeRef}
          data={data}
          openByDefault={false}
          idAccessor="path" // Tells it we want to use the "path" as the ID
          childrenAccessor="children" // Tells it we want to use the "children" as the children
          onToggle={handleToggle}
          rowHeight={32}
          indent={8}
          width={width}
          overscanCount={10}
          disableMultiSelection
          disableDrag
          selection={selectedPath}
          onSelect={(nodes: Array<NodeApi<BinaryTreeNodeData>>) => {
            const node = nodes[0];
            if (node) {
              setSelectedPath(node.data.path);
              //  TODO: callback or something to tell the rest of the app which node is selected.
              if (node.data.type === 'file') {
                setSelectedDepRevisionLocator(node.data.revisionId);
              }
              setSelectedNode(node);
            }
          }}
        >
          {BinaryTreeNode}
        </Tree>
      </div>
    </>
  );
};

export const ProjectBinaryTree: VoidFunctionComponent = () => {
  const addToast = useAddToast();
  const { replaceDrawer } = useDresser();

  const [selectedDepRevisionLocator, setSelectedDepRevisionLocator] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<NodeApi<BinaryTreeNodeData> | null>(null);
  const [data, setData] = useState<BinaryTreeNodeData[]>([ROOT_NODE]);
  const [pathsToOpen, setPathsToOpen] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const params = useParams<RevisionRouteParams>();
  const userRevisionLocator = `${params.project_id}$${params.revision_id}`;

  //  This is an esoteric use of React Query, typically you should never need to use queryClient directly in this way.
  //  However, doing it like this allows us to retain the benefits of React Query (like the caching)
  //  while still being able to imperatively make a new query when a new directory is toggled.
  //  Don't copy this pattern unless you have tried doing things the "normal" way first.
  const queryClient = useQueryClient();

  const onLoadPath = useCallback(async (path?: string, search?: string) => {
    let pathParam: GetBinaryComponentPathsParams = {};
    if (path) {
      pathParam = { path };
    }

    if (search) {
      pathParam.search = search;
    }

    //  `ensureQueryData` is an asynchronous function that can be used to get an existing query's cached data.
    //  If the query does not exist, queryClient.fetchQuery will be called and its results returned.
    const response = await queryClient.ensureQueryData({
      queryKey: getBinaryComponentPathsQueryKey(userRevisionLocator, pathParam),
      queryFn: () => getBinaryComponentPaths(userRevisionLocator, pathParam),
    });

    if (!response.data?.paths) {
      throw new Error(`Failed to load components for path: ${path}`);
    }

    return response.data.paths;
  }, [userRevisionLocator, queryClient]);

  // Preload directory tree on component mount
  useEffect(() => {
    const preloadTree = async () => {
      setLoading(true);
      try {
        const { tree, pathsToOpen: preloadedPaths } = await preloadDirectoryTree(onLoadPath);
        if (tree.length > 0) {
          const newRootNode = {
            ...ROOT_NODE,
            children: tree,
            hasLoaded: true,
          };
          setData([newRootNode]);
          setPathsToOpen([ROOT_NODE.path, ...preloadedPaths]);
        }
      } catch {
        addToast({
          type: 'error',
          content: `Failed to pre-load data.`,
        });
      } finally {
        setLoading(false);
      }
    };

    preloadTree();
  }, [userRevisionLocator, onLoadPath, addToast]);

  useEffect(() => {
    if (selectedDepRevisionLocator) {
      replaceDrawer(
        <ProjectDependencyDrawer
          onClose={() => setSelectedDepRevisionLocator(null)}
          projectRevisionLocator={userRevisionLocator}
          dependencyRevisionLocator={selectedDepRevisionLocator ?? ''}
          onDeleteDependency={() => {
            if (selectedNode) {
              setData(removeNodeFromParent(data, (selectedNode.data as BinaryFileNode).revisionId, 'file'));
              setSelectedDepRevisionLocator(null);
              setSelectedNode(null);
            }
          }}
          scope={{
            type: 'project',
            projectLocator: Locator.parsePackageString(userRevisionLocator),
            revisionLocator: userRevisionLocator,
          }}
        />,
        { onClose: () => setSelectedDepRevisionLocator(null) },
      );
    }
  }, [
    selectedDepRevisionLocator,
    data,
    replaceDrawer,
    userRevisionLocator,
    setSelectedDepRevisionLocator,
    setSelectedNode,
    selectedNode,
  ]);

  if (loading) {
    return <BinaryTreeLoading />;
  }

  return (
    <BinaryTree
      onLoadPath={onLoadPath}
      setSelectedDepRevisionLocator={setSelectedDepRevisionLocator}
      setSelectedNode={setSelectedNode}
      data={data}
      setData={setData}
      pathsToOpen={pathsToOpen}
    />
  );
};

export const ReleaseGroupBinaryTree: VoidFunctionComponent = () => {
  const addToast = useAddToast();
  const { replaceDrawer } = useDresser();

  const { projectGroupId } = useParams<Required<ReleaseGroupRouteParams>>();
  const [selectedDepRevisionLocator, setSelectedDepRevisionLocator] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<NodeApi<BinaryTreeNodeData> | null>(null);
  const releaseId = useSelectedReleaseId(toFiniteNumber(projectGroupId));
  const [data, setData] = useState<BinaryTreeNodeData[]>([ROOT_NODE]);
  const [pathsToOpen, setPathsToOpen] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  //  This is an esoteric use of React Query, typically you should never need to use queryClient directly in this way.
  //  However, doing it like this allows us to retain the benefits of React Query (like the caching)
  //  while still being able to imperatively make a new query when a new directory is toggled.
  //  Don't copy this pattern unless you have tried doing things the "normal" way first.
  const queryClient = useQueryClient();

  const onLoadPath = useCallback(async (path?: string, search?: string) => {
    if (!releaseId) {
      return [];
    }
    let pathParam: GetBinaryComponentPathsParams = {};
    if (path) {
      pathParam = { path };
    }

    if (search) {
      pathParam.search = search;
    }

    //  `ensureQueryData` is an asynchronous function that can be used to get an existing query's cached data.
    //  If the query does not exist, queryClient.fetchQuery will be called and its results returned.
    const response = await queryClient.ensureQueryData({
      queryKey: getReleaseGroupComponentPathsQueryKey(projectGroupId, releaseId, pathParam),
      queryFn: () => getReleaseGroupComponentPaths(projectGroupId, releaseId, pathParam),
    });

    if (!response.data?.paths) {
      throw new Error(`Failed to load components for path: ${path}`);
    }

    return response.data.paths;
  }, [projectGroupId, releaseId, queryClient]);

  // Preload directory tree on component mount
  useEffect(() => {
    const preloadTree = async () => {
      setLoading(true);
      try {
        const { tree, pathsToOpen: preloadedPaths } = await preloadDirectoryTree(onLoadPath);
        if (tree.length > 0) {
          const newRootNode = {
            ...ROOT_NODE,
            children: tree,
            hasLoaded: true,
          };
          setData([newRootNode]);
          setPathsToOpen([ROOT_NODE.path, ...preloadedPaths]);
        }
      } catch {
        addToast({
          type: 'error',
          content: `Failed to pre-load data.`,
        });
      } finally {
        setLoading(false);
      }
    };

    preloadTree();
  }, [projectGroupId, releaseId, onLoadPath, addToast]);

  useEffect(() => {
    if (selectedDepRevisionLocator && releaseId) {
      replaceDrawer(
        <ReleaseGroupDependencyDrawer
          onClose={() => setSelectedDepRevisionLocator(null)}
          releaseGroupId={projectGroupId}
          releaseGroupReleaseId={releaseId}
          dependencyRevisionLocator={selectedDepRevisionLocator ?? ''}
          onDeleteDependency={() => {
            if (selectedNode) {
              setData(removeNodeFromParent(data, (selectedNode.data as BinaryFileNode).revisionId, 'file'));
              setSelectedDepRevisionLocator(null);
              setSelectedNode(null);
            }
          }}
          scope={{
            type: 'releaseGroup',
            releaseGroupId: projectGroupId,
            releaseId,
          }}
        />,
      );
    }
  }, [
    selectedDepRevisionLocator,
    data,
    replaceDrawer,
    projectGroupId,
    releaseId,
    setSelectedDepRevisionLocator,
    setSelectedNode,
    selectedNode,
  ]);

  if (loading) {
    return <BinaryTreeLoading />;
  }

  return (
    <BinaryTree
      onLoadPath={onLoadPath}
      setSelectedDepRevisionLocator={setSelectedDepRevisionLocator}
      setSelectedNode={setSelectedNode}
      data={data}
      setData={setData}
      pathsToOpen={pathsToOpen}
    />
  );
};
